"use client";

import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import HistoryCard from "../HistoryCard/HistoryCard";
import useRendezvous from "../../../appointments/hooks/useRendezvous";
import useDoctors from "../../../authDoctor/hooks/useDoctors";
import "./PastAppointments.css";

const pickDoctorFields = (d) => ({
  doctorId: d?.id ?? d?.doctorId ?? d?.medecinId ?? d?.userId ?? d?._id ?? d?.pgId,
  fullName: d?.fullName ?? d?.name ?? d?.displayName ?? d?.doctorName ?? d?.username,
  specialty: d?.specialty ?? d?.speciality ?? d?.specialite ?? d?.title ?? d?.role,
  avatar: d?.photoUrl ?? d?.avatar ?? d?.image ?? d?.picture ?? d?.photo ?? null,
});

export default function PastAppointments({ limit = null, patientId }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  const navigate = useNavigate();
  const { getPastPatientAppointments } = useRendezvous();
  const { doctors, loading: doctorsLoading } = useDoctors();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [appointments, setAppointments] = useState([]);

  const didLoad = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);

    try {
      const items = await getPastPatientAppointments(patientId);
      setAppointments(Array.isArray(items) ? items : []);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("appointmentss.past.errors.loadFailed");
      setErr(msg);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, [getPastPatientAppointments, patientId, t]);

  useEffect(() => {
    if (didLoad.current) return;
    didLoad.current = true;
    load();
  }, [load]);

  const doctorById = useMemo(() => {
    const map = new Map();
    (doctors || []).forEach((d) => {
      const f = pickDoctorFields(d);
      if (f.doctorId != null) map.set(String(f.doctorId), f);
    });
    return map;
  }, [doctors]);

  const enrichedAppointments = useMemo(() => {
    const base = appointments || [];

    const joined = base.map((a) => {
      const key = String(a.medecinId ?? a.doctorId ?? "");
      const doc = key ? doctorById.get(key) : null;

      const providerDisplay =
        doc?.fullName || a.providerDisplay || a.provider || t("appointmentss.common.doctorFallback");
      const specialtyDisplay = doc?.specialty || a.specialtyDisplay || a.specialty || "";
      const providerImage = doc?.avatar || a.providerImage || a.image || null;

      return { ...a, providerDisplay, specialtyDisplay, providerImage };
    });

    if (limit && Number.isInteger(limit)) return joined.slice(0, limit);
    return joined;
  }, [appointments, doctorById, limit, t]);

  // ✅ REBOOK: redirige vers DoctorDetails
  const handleRebook = useCallback(
    (appointment) => {
      const docId = appointment?.medecinId ?? appointment?.doctorId;
      if (!docId) return;

      navigate(`/doctor-details/${docId}`, {
        state: {
          from: "rebook",
          previousAppointmentId: appointment?.id,
        },
      });
    },
    [navigate]
  );

  return (
    <div className="past-appointments-container" dir={isRTL ? "rtl" : "ltr"}>
      <div className="past-section-header">
        <h3 className="section-h3">{t("appointmentss.past.headerTitle")}</h3>

        {/* <div className="past-actions">
          <a href="/appointments/history" className="view-all">
            {t("appointmentss.past.viewAll")} {isRTL ? "←" : "→"}
          </a>
        </div> */}
      </div>

      <div className="past-appointments-list">
        {(loading || doctorsLoading) && (
          <div className="past-appointments-loading">
            {t("appointmentss.common.loading")}
          </div>
        )}

        {!loading && !doctorsLoading && err && (
          <div className="past-appointments-error">{err}</div>
        )}

        {!loading && !doctorsLoading && !err && enrichedAppointments.length === 0 && (
          <div className="past-appointments-empty">
            <CheckCircle /> <span>{t("appointmentss.past.empty")}</span>
          </div>
        )}

        {!loading && !doctorsLoading && !err && enrichedAppointments.length > 0 &&
          enrichedAppointments.map((a, idx) => (
            <HistoryCard
              key={a.id ?? `past-${idx}`}
              appointment={a}
              onRebook={() => handleRebook(a)}
            />
          ))}
      </div>
    </div>
  );
}
